import { Box, useColorModeValue, Spinner, Center } from '@chakra-ui/react'
import { useEffect, useRef, useState, memo } from 'react'

type PuzzleType = '222' | '333' | '444' | '555' | '666' | '777' | 'pyram' | 'skewb' | 'minx' | 'sq1' | 'clock'

interface ScramblePreviewProps {
  scramble: string
  size?: { width: string; height: string }
  checkered?: boolean
  visualization?: '2D' | '3D'
  event?: PuzzleType
}

// Load script only once at the application level
let scriptLoaded = false
const loadScriptPromise = new Promise((resolve) => {
  if (typeof window !== 'undefined') {
    const existingScript = document.querySelector('script[src*="scramble-display"]')
    if (existingScript) {
      scriptLoaded = true
      resolve(true)
    }
  }
})

const ScramblePreview = memo(({
  scramble,
  size = { width: '200px', height: '150px' },
  checkered = false,
  visualization = '2D',
  event = '333'
}: ScramblePreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(!scriptLoaded)
  const [hasError, setHasError] = useState(false)
  const bgColor = useColorModeValue('white', 'gray.800')

  useEffect(() => {
    let mounted = true
    
    const loadScrambleDisplay = async () => {
      try {
        if (!scriptLoaded) {
          const script = document.createElement('script')
          script.src = 'https://cdn.cubing.net/v0/js/scramble-display'
          script.type = 'module'
          document.head.appendChild(script)

          await new Promise((resolve) => {
            script.onload = resolve
          })
          scriptLoaded = true
        }

        if (containerRef.current && mounted) {
          // Clear previous content
          containerRef.current.innerHTML = ''
          
          // Create new scramble display element
          const scrambleElement = document.createElement('scramble-display')
          scrambleElement.setAttribute('scramble', scramble)
          scrambleElement.setAttribute('visualization', visualization)
          if (checkered) scrambleElement.setAttribute('checkered', '')
          scrambleElement.setAttribute('event', event)
          
          // Set size
          scrambleElement.style.width = size.width
          scrambleElement.style.height = size.height
          
          // Add to container
          containerRef.current.appendChild(scrambleElement)
          
          // Wait a bit for the element to render
          setTimeout(() => {
            if (mounted) {
              setIsLoading(false)
            }
          }, 100)
        }
      } catch (error) {
        console.error('Error loading scramble display:', error)
        if (mounted) {
          setIsLoading(false)
          setHasError(true)
        }
      }
    }

    loadScrambleDisplay()

    return () => {
      mounted = false
    }
  }, [scramble, size.width, size.height, checkered, visualization, event])

  // Don't render anything if CDN failed (graceful degradation)
  if (hasError) {
    return null
  }

  return (
    <Box
      position="relative"
      width={size.width}
      height={size.height}
    >
      <Box
        ref={containerRef}
        bg={bgColor}
        borderRadius="xl"
        overflow="hidden"
        width={size.width}
        height={size.height}
        opacity={isLoading ? 0 : 1}
        transition="opacity 0.3s ease-in-out"
      />
      {isLoading && (
        <Center
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg={bgColor}
          borderRadius="xl"
        >
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="rubiks.red"
            size="xl"
          />
        </Center>
      )}
    </Box>
  )
})

export default ScramblePreview 